/* eslint-disable max-lines-per-function */
import { useCallback, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import type React from 'react';

import type CropperType from 'cropperjs';
import { debounce } from 'lodash-es';
import Cropper from 'react-cropper';

import { useI18n } from '@change-corgi/core/react/i18n';
import { Global } from '@change-corgi/design-system-legacy/core';
import { Box } from '@change-corgi/design-system-legacy/layout';

import type { MediaDimensions, RawMediaCropInput } from '../../types';
import { GlobalStyle } from '../ImageCropperStyling';

import { useCropperRef } from './hooks/useCropperRef';

// The cropper element needs to be the same size as the uploader. To accomplish this,
// the same styling is passed to it.
const uploaderStyle = {
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	position: 'absolute' as const,
};

type CropperElementProps = {
	crop?: RawMediaCropInput;
	disabled?: boolean;
	image?: string;
	initialAspectRatio?: number;
	minContainerWidth?: number;
	updateCrop: (imageCrop: RawMediaCropInput) => void;
	updateImage?: (blob: Blob) => void;
	onInitialized?: (instance: CropperType) => void;
};

export const CropperElement: React.FC<CropperElementProps> = ({
	crop,
	disabled = false,
	image,
	initialAspectRatio = 16 / 9,
	minContainerWidth = 100,
	updateCrop,
	updateImage,
	onInitialized,
}) => {
	const cropperRef = useCropperRef();
	const { translate } = useI18n();

	useEffect(() => {
		const cropper = cropperRef.current?.cropper;
		if (typeof cropper !== 'undefined') {
			if (disabled) cropperRef.current?.cropper.disable();
			else cropperRef.current?.cropper.enable();
		}
	}, [disabled, cropperRef]);

	const refForFocus = useCallback((node: HTMLDivElement | null) => {
		// this will trigger an autofocus on first render only
		node && node.focus();
	}, []);

	// because of the view/drag mode, we set the initial crop by moving the image on the canvas
	const setCanvasCrop = useCallback(() => {
		if (crop) {
			const cropper = cropperRef.current?.cropper;
			if (typeof cropper !== 'undefined') {
				const dimensions: MediaDimensions = cropper.getCanvasData();
				const scale = dimensions.width / crop.w;
				const data = {
					left: -crop.x * scale,
					top: -crop.y * scale,
					width: dimensions.width,
					height: dimensions.height,
				};
				cropper.setCanvasData(data);
			}
		}
	}, [crop, cropperRef]);

	const moveCropper = useCallback(
		(event: KeyboardEvent<HTMLDivElement>, x: number, y: number) => {
			event.preventDefault();
			cropperRef.current?.cropper.move(x, y);
		},
		[cropperRef],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!disabled) {
				switch (event.key) {
					case 'ArrowLeft':
						moveCropper(event, -1, 0);
						break;
					case 'ArrowRight':
						moveCropper(event, 1, 0);
						break;
					case 'ArrowDown':
						moveCropper(event, 0, 1);
						break;
					case 'ArrowUp':
						moveCropper(event, 0, -1);
						break;
					default:
						break;
				}
			}
		},
		[disabled, moveCropper],
	);

	// TODO: investigate why `onCrop` still gets called after its unmounted, leading to a potential
	// memory leak
	// To ensure we save the desired repositioned image, we update Uppy with the new cropped image from Cropper.
	// Testing library's drag and drop is not supported by jsdom
	/* istanbul ignore next */
	const onCrop = debounce(() => {
		const cropper = cropperRef.current?.cropper;

		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (typeof cropper !== 'undefined' && cropper.getCroppedCanvas() !== null) {
			// we base the crop on the full image size, so translation happens here
			const {
				left,
				top,
				width: croppedWidth,
				height: croppedHeight,
				naturalWidth,
				naturalHeight,
			} = cropper.getCanvasData();
			const croppedDimensions = cropper.getCroppedCanvas();

			const cropData = {
				x: -(left * (naturalHeight / croppedHeight)),
				y: -(top * (naturalWidth / croppedWidth)),
				w: naturalWidth,
				h: naturalHeight,
				croppedHeight,
				croppedWidth,
				newHeight: croppedDimensions.height,
				newWidth: croppedDimensions.width,
			};
			updateCrop(cropData);
			cropper.getCroppedCanvas().toBlob((blob) => {
				if (blob && updateImage) updateImage(blob);
			}, 'image/jpeg');
		}
	}, 250);

	useEffect(() => {
		return () => {
			cropperRef.current = null;
		};
	}, [cropperRef]);

	return (
		<>
			<GlobalStyle />
			<Box
				ref={refForFocus}
				role="button"
				data-testid="cropper-container"
				tabIndex={0}
				aria-label={translate('corgi.sap.media.a11y.cropper.crop_aria_label')}
				onKeyDown={handleKeyDown}
				sx={{
					height: '100%',
					padding: '1px',
					marginLeft: '-1px',
					'&:focus': {
						outline: '2px solid hsla(305, 6%, 22%, 0.185)',
						outlineOffset: '1px',
					},
				}}
			>
				<Global
					styles={{
						'.cropper-view-box': {
							outline: 'none',
						},
					}}
				/>
				<Cropper
					autoCropArea={1}
					src={image}
					background={false}
					center={false}
					checkOrientation
					crop={onCrop}
					cropBoxMovable={false}
					cropBoxResizable={false}
					dragMode="move"
					guides={false}
					highlight={false}
					initialAspectRatio={initialAspectRatio}
					width="100%"
					ready={setCanvasCrop}
					ref={cropperRef}
					restore={false}
					scalable={false}
					sx={uploaderStyle}
					toggleDragModeOnDblclick={false}
					viewMode={3}
					zoomable={false}
					onInitialized={onInitialized}
					minContainerWidth={minContainerWidth}
				/>
			</Box>
		</>
	);
};
